import { Box, CircularProgress } from '@mui/material';
import React, { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';


import { NavigationItem } from './NavigationItem';
import { ChevronRightIcon, LockIcon } from './SidebarIcons';

import { NavItem } from '@/components/ds';
import { withViewTransition } from '@/utils/viewTransition';

// ── Types ────────────────────────────────────────────────────────────────────
export interface SubNavItem {
  path: string;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  lock?: boolean;
  external?: boolean;
  badge?: number;
  processing?: boolean;
  onClick?: () => void;
}

export interface NavGroupProps {
  label: string;
  icon?: React.ReactNode;
  items: SubNavItem[];
  /**
   * When true, the section header is clickable and shows a chevron on
   * the right side. Used in owner mode to demote sections that are no
   * longer day-to-day (Due Diligence, Transaction) — they collapse by
   * default so the unlocked Homeowner / Tools rise to the top of the
   * nav.
   */
  collapsible?: boolean;
  /** Initial open/closed state when `collapsible` is true. Ignored otherwise. */
  defaultCollapsed?: boolean;
}

// ── Processing spinner for nav items ────────────────────────────────────────
const ProcessingSpinner = () => (
  <Box sx={{ position: 'relative', width: 14, height: 14, flexShrink: 0 }}>
    <CircularProgress
      variant="determinate"
      value={100}
      size={14}
      thickness={4}
      sx={{ color: 'var(--tx-fill-2)', position: 'absolute', top: 0, left: 0 }}
    />
    <CircularProgress
      variant="indeterminate"
      size={14}
      thickness={4}
      sx={{
        color: 'var(--tx-lime-ink)',
        position: 'absolute',
        top: 0,
        left: 0,
        animationDuration: '1.4s',
      }}
    />
  </Box>
);

// ── Section header (shared typography) ───────────────────────────────────────
export const SectionHeader = ({
  icon,
  children,
  trailing,
  onClick,
}: {
  icon?: React.ReactNode;
  children: React.ReactNode;
  /** Optional element rendered right-aligned (e.g. collapse chevron). */
  trailing?: React.ReactNode;
  /** When set, the header is clickable. */
  onClick?: () => void;
}) => (
  <Box
    onClick={onClick}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      px: '10px',
      pt: '6px',
      pb: '8px',
      fontWeight: 600,
      fontSize: 12,
      color: 'var(--tx-faint)',
      lineHeight: 1,
      cursor: onClick ? 'pointer' : 'default',
      userSelect: onClick ? 'none' : 'auto',
    }}>
    {icon && <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{icon}</Box>}
    <Box sx={{ flex: 1, minWidth: 0 }}>{children}</Box>
    {trailing && <Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{trailing}</Box>}
  </Box>
);

// ── Collapsed individual icon item ───────────────────────────────────────────
export const CollapsedNavItem = ({
  path,
  label,
  icon,
  disabled,
  external,
  badge,
  onClick,
}: {
  path: string;
  label: string;
  icon: React.ReactNode;
  disabled?: boolean;
  external?: boolean;
  badge?: number;
  onClick?: () => void;
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = location.pathname === path;

  const handleClick = () => {
    if (disabled) return;
    if (onClick) {
      onClick();
      return;
    }
    if (external) window.open(path, '_blank');
    else withViewTransition(() => navigate(path));
  };

  return (
    <Box sx={{ px: '8px', mb: '4px' }}>
      <NavItem
        collapsed
        label={label}
        icon={icon}
        active={isActive}
        disabled={disabled}
        badge={badge}
        onClick={handleClick}
        testId={`nav-item-${label.toLowerCase().replace(/\s+/g, '-')}`}
      />
    </Box>
  );
};

// ── Expanded group (section header + vertical line) ──────────────────────────
export const ExpandedNavGroup = ({
  label,
  icon,
  items,
  collapsible = false,
  defaultCollapsed = false,
}: NavGroupProps) => {
  const [collapsed, setCollapsed] = useState(collapsible && defaultCollapsed);
  const isOpen = !collapsible || !collapsed;

  const chevron = collapsible ? (
    <Box
      sx={{
        display: 'inline-flex',
        transform: isOpen ? 'rotate(90deg)' : 'rotate(0deg)',
        transition: 'transform 120ms ease',
        color: 'var(--tx-muted)',
      }}>
      <ChevronRightIcon size={12} />
    </Box>
  ) : undefined;

  return (
    <Box sx={{ mb: '20px' }} data-testid={`nav-group-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <SectionHeader
        icon={icon}
        trailing={chevron}
        onClick={collapsible ? () => setCollapsed((c) => !c) : undefined}>
        {label}
      </SectionHeader>
      {isOpen && (
        // a hair of space between rows, so a hovered row never runs into the selected one
        <Box sx={{ ml: 0, pl: 0, display: 'flex', flexDirection: 'column', gap: '3px' }}>
          {items.map((item) => (
            <NavigationItem
              key={item.path}
              path={item.path}
              label={item.label}
              icon={item.icon}
              disabled={item.disabled}
              external={item.external}
              badge={item.badge}
              onClick={item.onClick}
              endIcon={
                item.lock ? <LockIcon color="var(--tx-faint)" /> :
                item.processing ? <ProcessingSpinner /> :
                undefined
              }
              compact
            />
          ))}
        </Box>
      )}
    </Box>
  );
};

// ── Divider ──────────────────────────────────────────────────────────────────
export const NavDivider = () => (
  <Box
    sx={{
      mx: '10px',
      mt: '2px',
      mb: '14px',
      borderTop: '1px solid var(--tx-line)',
    }}
  />
);
